import type React from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface AnchorPosition {
  top: number;
  left: number;
}

interface FilterPopoverContextType {
  enabled: boolean;
  isOpen: boolean;
  anchorPosition: AnchorPosition | undefined;
  open: (el: HTMLElement) => void;
  close: () => void;
}

const FilterPopoverContext = createContext<FilterPopoverContextType>({
  enabled: false,
  isOpen: false,
  anchorPosition: undefined,
  open: () => {},
  close: () => {},
});

export const FilterPopoverProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [anchorPosition, setAnchorPosition] = useState<AnchorPosition | undefined>(undefined);

  const open = useCallback((el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setAnchorPosition({ top: rect.bottom, left: rect.right });
  }, []);

  const close = useCallback(() => {
    setAnchorPosition(undefined);
  }, []);

  const value = useMemo(
    () => ({ enabled: true, isOpen: anchorPosition !== undefined, anchorPosition, open, close }),
    [anchorPosition, open, close],
  );

  return <FilterPopoverContext.Provider value={value}>{children}</FilterPopoverContext.Provider>;
};

export const useFilterPopover = (): FilterPopoverContextType => useContext(FilterPopoverContext);
